/**
 * Firestore persistence for planned cash obligations (bonus + tax).
 */
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  BonusPlan,
  BonusPayoutSlice,
  BonusPlanMode,
  BonusPositionOverride,
  ObligationSettings,
  TaxPlan,
} from '../types/finances';
import {
  createDefaultBonusPlan,
  createDefaultTaxPlan,
  DEFAULT_BONUS_PERCENT,
  DEFAULT_OBLIGATION_SETTINGS,
  DEFAULT_OWNER_FEDERAL_EFFECTIVE_RATE,
  DEFAULT_QBI_RATE,
  DEFAULT_TN_EXCISE_RATE,
  DEFAULT_TN_FRANCHISE_MIN,
  DEFAULT_TN_FRANCHISE_PER_HUNDRED,
} from '../types/finances';

const SETTINGS_DOC = 'obligationSettings';

function tsToDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return null;
}

function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromIsoDate(s: string, fallback: Date): Date {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return fallback;
  return new Date(y, m - 1, d);
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function parseObligationSettings(data: Record<string, unknown> | undefined): ObligationSettings {
  if (!data) return { ...DEFAULT_OBLIGATION_SETTINGS };
  const bonus = (data.bonusDefaults || {}) as Record<string, unknown>;
  const tax = (data.taxDefaults || {}) as Record<string, unknown>;
  return {
    entityType: 'llc_s_corp',
    state: typeof data.state === 'string' && data.state.trim() ? data.state.trim().toUpperCase() : 'TN',
    fiscalYearEndMonth: Math.min(12, Math.max(1, asNumber(data.fiscalYearEndMonth, 12))),
    bonusDefaults: {
      defaultPercent: asNumber(bonus.defaultPercent, DEFAULT_BONUS_PERCENT),
      payoutMonth: Math.min(12, Math.max(1, asNumber(bonus.payoutMonth, 12))),
      payoutDay: Math.min(31, Math.max(1, asNumber(bonus.payoutDay, 15))),
      includeEmployerTax: asBool(bonus.includeEmployerTax, true),
    },
    taxDefaults: {
      ownerFederalEffectiveRate: asNumber(tax.ownerFederalEffectiveRate, DEFAULT_OWNER_FEDERAL_EFFECTIVE_RATE),
      qbiEnabled: asBool(tax.qbiEnabled, true),
      includeOwnerTaxDistributions: asBool(tax.includeOwnerTaxDistributions, true),
    },
    updatedAt: tsToDate(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
  };
}

function parseOverride(raw: unknown): BonusPositionOverride | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.positionId !== 'string' || !o.positionId) return null;
  return {
    positionId: o.positionId,
    included: asBool(o.included, true),
    percent: typeof o.percent === 'number' ? o.percent : null,
    amount: typeof o.amount === 'number' ? o.amount : null,
  };
}

function parseBonusSlice(raw: unknown, year: number): BonusPayoutSlice | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const fallback = new Date(year, 11, 15);
  const date = typeof o.date === 'string'
    ? fromIsoDate(o.date, fallback)
    : tsToDate(o.date) ?? fallback;
  return {
    id: typeof o.id === 'string' ? o.id : `bs-${Math.random().toString(36).slice(2, 8)}`,
    date,
    percent: asNumber(o.percent, 0),
    label: typeof o.label === 'string' ? o.label : undefined,
  };
}

export function parseBonusPlan(
  year: number,
  data: Record<string, unknown> | undefined,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): BonusPlan {
  const fallback = createDefaultBonusPlan(year, settings);
  if (!data) return fallback;
  const mode = data.mode;
  const validMode: BonusPlanMode =
    mode === 'fixed'
    || mode === 'profit_pool'
    || mode === 'percent_of_salary'
    || mode === 'percent_of_profit'
      ? mode
      : 'percent_of_profit';
  return {
    year: asNumber(data.year, year),
    enabled: asBool(data.enabled, true),
    mode: validMode,
    defaultPercent: asNumber(data.defaultPercent, fallback.defaultPercent),
    defaultFixedAmount: asNumber(data.defaultFixedAmount, 0),
    poolAmount: asNumber(data.poolAmount, 0),
    includeEmployerTax: asBool(data.includeEmployerTax, fallback.includeEmployerTax),
    payoutSplit: data.payoutSplit === 'custom' ? 'custom' : 'lump',
    payoutMonth: Math.min(12, Math.max(1, asNumber(data.payoutMonth, fallback.payoutMonth))),
    payoutDay: Math.min(31, Math.max(1, asNumber(data.payoutDay, fallback.payoutDay))),
    customSlices: Array.isArray(data.customSlices)
      ? data.customSlices.map((s) => parseBonusSlice(s, year)).filter((s): s is BonusPayoutSlice => !!s)
      : [],
    overrides: Array.isArray(data.overrides)
      ? data.overrides.map(parseOverride).filter((o): o is BonusPositionOverride => !!o)
      : [],
    updatedAt: tsToDate(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
  };
}

export function parseTaxPlan(
  year: number,
  data: Record<string, unknown> | undefined,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): TaxPlan {
  const fallback = createDefaultTaxPlan(year, settings);
  if (!data) return fallback;
  return {
    year: asNumber(data.year, year),
    enabled: asBool(data.enabled, true),
    estimatedAnnualProfit: asNumber(data.estimatedAnnualProfit, 0),
    estimatedNetWorth: asNumber(data.estimatedNetWorth, 0),
    ownerFederalEffectiveRate: asNumber(data.ownerFederalEffectiveRate, fallback.ownerFederalEffectiveRate),
    qbiEnabled: asBool(data.qbiEnabled, fallback.qbiEnabled),
    qbiRate: asNumber(data.qbiRate, DEFAULT_QBI_RATE),
    includeOwnerTaxDistributions: asBool(data.includeOwnerTaxDistributions, fallback.includeOwnerTaxDistributions),
    tnExciseRate: asNumber(data.tnExciseRate, DEFAULT_TN_EXCISE_RATE),
    tnFranchisePerHundred: asNumber(data.tnFranchisePerHundred, DEFAULT_TN_FRANCHISE_PER_HUNDRED),
    tnFranchiseMin: asNumber(data.tnFranchiseMin, DEFAULT_TN_FRANCHISE_MIN),
    updatedAt: tsToDate(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
  };
}

export function serializeBonusPlan(plan: BonusPlan) {
  return {
    year: plan.year,
    enabled: plan.enabled,
    mode: plan.mode,
    defaultPercent: plan.defaultPercent,
    defaultFixedAmount: plan.defaultFixedAmount,
    poolAmount: plan.poolAmount,
    includeEmployerTax: plan.includeEmployerTax,
    payoutSplit: plan.payoutSplit,
    payoutMonth: plan.payoutMonth,
    payoutDay: plan.payoutDay,
    customSlices: plan.customSlices.map((s) => ({
      id: s.id,
      date: toIsoDate(s.date),
      percent: s.percent,
      label: s.label ?? null,
    })),
    overrides: plan.overrides,
    updatedAt: serverTimestamp(),
  };
}

export function serializeTaxPlan(plan: TaxPlan) {
  return {
    year: plan.year,
    enabled: plan.enabled,
    estimatedAnnualProfit: plan.estimatedAnnualProfit,
    estimatedNetWorth: plan.estimatedNetWorth,
    ownerFederalEffectiveRate: plan.ownerFederalEffectiveRate,
    qbiEnabled: plan.qbiEnabled,
    qbiRate: plan.qbiRate,
    includeOwnerTaxDistributions: plan.includeOwnerTaxDistributions,
    tnExciseRate: plan.tnExciseRate,
    tnFranchisePerHundred: plan.tnFranchisePerHundred,
    tnFranchiseMin: plan.tnFranchiseMin,
    updatedAt: serverTimestamp(),
  };
}

export function subscribeObligationSettings(
  onNext: (settings: ObligationSettings) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'financeSettings', SETTINGS_DOC),
    (snap) => onNext(parseObligationSettings(snap.data() as Record<string, unknown> | undefined)),
    (err) => onError?.(err),
  );
}

export function subscribeBonusPlan(
  year: number,
  settings: ObligationSettings,
  onNext: (plan: BonusPlan) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'bonusPlans', String(year)),
    (snap) => onNext(parseBonusPlan(year, snap.data() as Record<string, unknown> | undefined, settings)),
    (err) => onError?.(err),
  );
}

export function subscribeTaxPlan(
  year: number,
  settings: ObligationSettings,
  onNext: (plan: TaxPlan) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'taxPlans', String(year)),
    (snap) => onNext(parseTaxPlan(year, snap.data() as Record<string, unknown> | undefined, settings)),
    (err) => onError?.(err),
  );
}

export async function saveObligationSettings(
  settings: ObligationSettings,
  updatedBy: string,
): Promise<void> {
  await setDoc(doc(db, 'financeSettings', SETTINGS_DOC), {
    entityType: settings.entityType,
    state: settings.state,
    fiscalYearEndMonth: settings.fiscalYearEndMonth,
    bonusDefaults: settings.bonusDefaults,
    taxDefaults: settings.taxDefaults,
    updatedAt: serverTimestamp(),
    updatedBy,
  }, { merge: true });
}

export async function saveBonusPlan(plan: BonusPlan, updatedBy: string): Promise<void> {
  await setDoc(doc(db, 'bonusPlans', String(plan.year)), {
    ...serializeBonusPlan(plan),
    updatedBy,
  }, { merge: true });
}

export async function saveTaxPlan(plan: TaxPlan, updatedBy: string): Promise<void> {
  await setDoc(doc(db, 'taxPlans', String(plan.year)), {
    ...serializeTaxPlan(plan),
    updatedBy,
  }, { merge: true });
}
